import { ActionIcon, Badge, Tooltip } from "@mantine/core";
import { CSSProperties, type JSX, MouseEventHandler, ReactNode } from "react";

import SvgIconRaw from "~/components/SvgIconRaw";
import { useSemanticColors } from "~/utils/Theme/useSemanticColors";

export type ChipProps = {
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  variant?: "filled" | "outlined";
  size?: "small" | "medium";
  label: string | JSX.Element | ReactNode;
  style?: CSSProperties;
  className?: string;
  tooltipTitle?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDelete?: MouseEventHandler<HTMLButtonElement>;
  "data-testid"?: string;
};

const Chip = ({
  color,
  variant,
  size,
  label,
  style,
  className,
  tooltipTitle,
  onClick,
  onDelete,
  ...rest
}: ChipProps) => {
  const colors = useSemanticColors();

  // Named colors reproduce the MUI chip palette roles; the default look is
  // the neutral bg-secondary/border-secondary pill used across the app.
  const named: Record<string, string> = {
    primary: "var(--mantine-primary-color-filled)",
    secondary: colors.myfg.secondary,
    error: colors.myfg.errorPrimary,
    info: colors.utility.blue[600],
    success: colors.myfg.successPrimary,
    warning: colors.myfg.warningPrimary,
  };
  const namedColor = color && color !== "default" ? named[color] : undefined;
  const filled = variant === "filled";

  const chipStyle: CSSProperties = {
    height: size === "medium" ? 32 : 24,
    paddingLeft: 8,
    paddingRight: onDelete ? 4 : 8,
    fontSize: "0.8125rem",
    fontWeight: 400,
    textTransform: "none",
    cursor: onClick ? "pointer" : undefined,
    border: `1px solid ${namedColor ?? "var(--mantine-color-border-secondary)"}`,
    backgroundColor: filled
      ? (namedColor ?? "var(--mantine-color-bg-secondary)")
      : namedColor
        ? "transparent"
        : "var(--mantine-color-bg-secondary)",
    color: filled
      ? colors.mytext.primaryOnBrand
      : (namedColor ?? "var(--mantine-color-text-primary)"),
    ...style,
  };

  const badge = (
    <Badge
      variant="outline"
      radius="xl"
      className={className}
      style={chipStyle}
      onClick={onClick}
      // clickable MUI chips exposed role="button"; keep that for a11y + tests
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      rightSection={
        onDelete ? (
          <ActionIcon
            variant="transparent"
            size={16}
            c="inherit"
            aria-label="remove"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(event);
            }}
          >
            <SvgIconRaw name="XClose" size={14} />
          </ActionIcon>
        ) : undefined
      }
      data-testid={rest["data-testid"]}
    >
      {label}
    </Badge>
  );

  return tooltipTitle ? <Tooltip label={tooltipTitle}>{badge}</Tooltip> : badge;
};

export type ChipPredefinedProps = ChipProps & {
  type: "error" | "info" | "warning" | "success" | "successContained";
};

export const ChipPredefined = ({
  type,
  style,
  ...rest
}: ChipPredefinedProps) => {
  const colors = useSemanticColors();

  switch (type) {
    case "error":
      return (
        <Chip
          color="error"
          size="small"
          style={{
            color: colors.utility.error[700],
            backgroundColor: colors.utility.error[50],
            borderColor: colors.utility.error[200],
            ...style,
          }}
          {...rest}
        />
      );
    case "info":
      return (
        <Chip
          color="info"
          size="small"
          style={{
            color: colors.utility.blue[700],
            backgroundColor: colors.utility.blue[50],
            borderColor: colors.utility.blue[200],
            ...style,
          }}
          {...rest}
        />
      );
    case "warning":
      return (
        <Chip
          color="warning"
          size="small"
          style={{
            color: colors.utility.warning[700],
            backgroundColor: colors.utility.warning[50],
            borderColor: colors.utility.warning[200],
            ...style,
          }}
          {...rest}
        />
      );
    case "success":
      return (
        <Chip
          color="success"
          size="small"
          style={{
            color: colors.utility.success[700],
            backgroundColor: colors.utility.success[50],
            borderColor: colors.utility.success[200],
            ...style,
          }}
          {...rest}
        />
      );
    case "successContained":
      return (
        <Chip
          variant="filled"
          color="success"
          size="small"
          style={{
            color: colors.mytext.primaryOnBrand,
            backgroundColor: colors.utility.success[500],
            borderColor: colors.utility.success[700],
            ...style,
          }}
          {...rest}
        />
      );
    default:
      return null;
  }
};

export default Chip;
